"use client";

import { InputEntryFunctionData } from "@aptos-labs/ts-sdk";
import type { CampaignSummary } from "@decibeltrade/sdk";
import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import { toast } from "@/components/toast";
import { useAnalytics } from "@/contexts/analytics-context";
import { readDexSdk, sdkConfig } from "@/contexts/dex-sdk";
import { applyOptimisticClaim } from "@/contexts/mutations/campaigns/optimistic-claim";
import { useWalletFeePayerWithFallback } from "@/contexts/mutations/wallet-fee-payer";
import { campaignSummaryQueryKey } from "@/contexts/queries/campaigns/use-campaign-summary";
import { useAuthState } from "@/hooks/use-auth-state";
import type { RewardsClaimEventProps } from "@/lib/analytics";
import { parseClaimCampaignAbort } from "@/utils/move-abort";
import { getServerTime } from "@/utils/server-time";

type ClaimArgs = RewardsClaimEventProps;

export function useClaimCampaignReward() {
  const queryClient = useQueryClient();
  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();
  const { accountAddress } = useAuthState();
  const analytics = useAnalytics();
  const t = useTranslations("rewards.toast");

  return useMutation({
    mutationFn: async (args: ClaimArgs) => {
      analytics.rewards.claimInitiated(args);

      const data: InputEntryFunctionData = {
        function: `${sdkConfig.deployment.campaignPackage}::campaign_manager::claim_by_id`,
        typeArguments: [],
        functionArguments: [args.campaignId],
      };

      const pendingTransaction = await signAndSubmitWalletTransaction({ data });
      await readDexSdk.deps.aptos.waitForTransaction({
        transactionHash: pendingTransaction.hash,
      });
      return { txHash: pendingTransaction.hash };
    },
    onSuccess: ({ txHash }, args) => {
      toast({
        title: t("claimSuccessTitle"),
        description: t("claimSuccessDescription"),
        variant: "success",
      });
      applyOptimistic(queryClient, accountAddress, args.campaignId, txHash);
      analytics.rewards.claimSucceeded({ ...args, txHash });
    },
    onError: (error: Error, args) => {
      const code = parseClaimCampaignAbort(error);
      toast({
        title: t("claimErrorTitle"),
        description: code ? t(`errors.${code}`) : t("errors.unknown"),
        variant: "error",
      });
      analytics.rewards.claimFailed({ ...args, errorCode: code, error });
    },
  });
}

function applyOptimistic(
  queryClient: QueryClient,
  accountAddress: string | undefined,
  campaignId: number,
  txHash: string,
) {
  if (!accountAddress) {
    return;
  }
  const nowSec = Math.floor(getServerTime() / 1000);
  queryClient.setQueryData(
    campaignSummaryQueryKey({ accountAddress }),
    (prev: CampaignSummary | undefined) =>
      prev ? applyOptimisticClaim(prev, { campaignId, txHash, nowSec }) : prev,
  );
}
